import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import StatisticsGlassCard from './StatisticsGlassCard';
import { DESIGN_TOKENS } from '../../constants/designTokens';
import StatisticsDesktopSectionHeader from './StatisticsDesktopSectionHeader';
import { STATS_ICONS } from './statisticsAssets';
import useChartContainerReady from './useChartContainerReady';

const FAITH_COLORS = {
  new: '#10B981',   // Emerald
  core: '#F59E0B',  // Amber
  care: '#EF4444',  // Red
};

export default function StatisticsDesktopFaithDemographicsCard({
  faithStats,
  filteredStudentsLength,
  isEditMode,
  longPressHandlers,
}) {
  const chartData = [
    { name: '새가족', value: faithStats.new, color: FAITH_COLORS.new },
    { name: '기존', value: faithStats.core, color: FAITH_COLORS.core },
    { name: '관심', value: faithStats.needCare, color: FAITH_COLORS.care },
  ].filter((d) => d.value > 0);
  const { containerRef, isReady } = useChartContainerReady();

  return (
    <StatisticsGlassCard className="h-full p-6 flex flex-col items-center justify-between relative" {...longPressHandlers} isEditMode={isEditMode}>
      <div className="w-full">
        <StatisticsDesktopSectionHeader
          iconSrc={STATS_ICONS.fire}
          title="신앙 단계"
          badge={`${filteredStudentsLength}명`}
        />
      </div>

      <div ref={containerRef} className="relative w-full flex-1 min-h-[160px] min-w-0">
        {isReady ? (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
            <PieChart>
              <defs>
                {chartData.map((entry, i) => (
                  <linearGradient key={`grad-${i}`} id={`faithGrad-${i}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                    <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={68}
                paddingAngle={4}
                dataKey="value"
                animationBegin={0}
                animationDuration={1200}
                animationEasing="ease-out"
                cornerRadius={4}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`url(#faithGrad-${index})`} stroke="none" />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(255,255,255,0.98)',
                  border: 'none',
                  borderRadius: '16px',
                  boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
                  padding: '10px 14px',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
                formatter={(value, name) => [`${value}명`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : null}
        {/* Central metric */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-3xl font-black text-gray-800 font-do-hyeon">{filteredStudentsLength}</span>
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">TOTAL</span>
        </div>
      </div>

      {/* Legend row */}
      <div className="grid grid-cols-3 gap-2 w-full mt-3">
        <div className="bg-emerald-50/60 border border-emerald-100 p-2.5 rounded-2xl text-center">
          <div className="text-lg font-black text-emerald-600">{faithStats.new}</div>
          <div className="text-[10px] font-bold text-emerald-500/80">새가족</div>
        </div>
        <div className="bg-amber-50/60 border border-amber-100 p-2.5 rounded-2xl text-center">
          <div className="text-lg font-black text-amber-500">{faithStats.core}</div>
          <div className="text-[10px] font-bold text-amber-400/80">기존</div>
        </div>
        <div className="bg-rose-50/60 border border-rose-100 p-2.5 rounded-2xl text-center">
          <div className="text-lg font-black text-rose-500">{faithStats.needCare}</div>
          <div className="text-[10px] font-bold text-rose-400/80">관심</div>
        </div>
      </div>
    </StatisticsGlassCard>
  );
}
